import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import useFinanceSummary from '../hooks/useFinanceSummary';
import useProductMarginsPreview from '../hooks/useProductMarginsPreview';
import './OverviewTab.css';

const PERIODS = [
  { id: '7d',   label: '7d' },
  { id: '30d',  label: '30d' },
  { id: '90d',  label: '90d' },
  { id: 'year', label: 'Year' },
];

/* ─── Formatting helpers (module scope) ────────────────────────── */

const NAIRA = new Intl.NumberFormat('en-NG', {
  style: 'currency',
  currency: 'NGN',
  maximumFractionDigits: 0,
});

const COMPACT = new Intl.NumberFormat('en-NG', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const fmtMoney  = (n) => NAIRA.format(Number(n || 0));
const fmtCompact = (n) => '₦' + COMPACT.format(Number(n || 0));
const fmtPct    = (n) => `${Number(n || 0).toFixed(1)}%`;

/* ─── Module-scope sub-components ───────────────────────────────
   Defined at module scope (not inside OverviewTab) so React doesn't
   unmount + remount their subtrees on every parent render — that's
   the input-focus-loss trap from feedback_no_inline_components.md. */

function PeriodSwitcher({ value, onChange }) {
  return (
    <div className="fin-period-switcher" role="radiogroup" aria-label="Time period">
      {PERIODS.map((p) => (
        <button
          key={p.id}
          type="button"
          role="radio"
          aria-checked={value === p.id}
          className={`fin-period-pill${value === p.id ? ' is-active' : ''}`}
          onClick={() => onChange(p.id)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function KpiCard({ label, value, hint, tone = 'default', loading = false }) {
  if (loading) {
    return (
      <div className="fin-kpi fin-kpi--skeleton">
        <div className="fin-kpi-label" />
        <div className="fin-kpi-value" />
        <div className="fin-kpi-hint" />
      </div>
    );
  }
  return (
    <div className={`fin-kpi fin-kpi--${tone}`}>
      <p className="fin-kpi-label">{label}</p>
      <p className="fin-kpi-value">{value}</p>
      {hint && <p className="fin-kpi-hint">{hint}</p>}
    </div>
  );
}

function TrendChart({ data }) {
  // Cap empty data with a friendly placeholder rather than render an empty SVG.
  if (!data || data.length === 0) {
    return (
      <div className="fin-chart-empty">
        <p>No profit data yet for this period.</p>
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={(d) =>
            new Date(d).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })
          }
          tick={{ fontSize: 11, fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={(v) => fmtCompact(v)}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
          width={56}
        />
        <Tooltip
          formatter={(v) => fmtMoney(v)}
          labelFormatter={(d) =>
            new Date(d).toLocaleDateString('en-NG', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })
          }
          contentStyle={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            fontSize: 12,
          }}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#0d7463"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, fill: '#0d7463' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function TopMarginsList({ items, loading, error }) {
  if (loading) {
    return (
      <div className="fin-top-list">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="fin-top-row fin-top-row--skeleton">
            <div className="fin-top-thumb" />
            <div className="fin-top-meta">
              <div className="fin-top-name" />
              <div className="fin-top-sub" />
            </div>
            <div className="fin-top-margin" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <p className="fin-list-error">{error}</p>;
  }

  if (!items || items.length === 0) {
    return (
      <p className="fin-list-empty">
        No product sales yet for this period.
      </p>
    );
  }

  return (
    <div className="fin-top-list">
      {items.map((item) => (
        <div key={item.productId} className="fin-top-row">
          {item.image ? (
            <img className="fin-top-thumb" src={item.image} alt="" />
          ) : (
            <div className="fin-top-thumb fin-top-thumb--placeholder" aria-hidden />
          )}
          <div className="fin-top-meta">
            <p className="fin-top-name" title={item.name}>{item.name}</p>
            <p className="fin-top-sub">
              {item.unitsSold} sold · {fmtCompact(item.revenue)} rev
            </p>
          </div>
          <span
            className={`fin-top-margin ${item.marginPct >= 0 ? 'is-positive' : 'is-negative'}`}
          >
            {fmtPct(item.marginPct)}
          </span>
        </div>
      ))}
    </div>
  );
}

function EmptyOverview() {
  return (
    <div className="fin-empty">
      <Sparkles size={40} className="fin-empty-icon" />
      <h2 className="fin-empty-title">Your Finance dashboard fills as you sell</h2>
      <p className="fin-empty-sub">
        Place orders through your storefront, POS, WhatsApp, or by recording a
        sale manually. Revenue, profit and expense KPIs will appear here in
        real time.
      </p>
      <Link to="/products" className="fin-empty-cta">Add a product</Link>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="fin-error-banner" role="alert">
      <AlertCircle size={18} />
      <span>{message}</span>
      <button type="button" className="fin-error-retry" onClick={onRetry}>
        <RefreshCw size={14} /> Retry
      </button>
    </div>
  );
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function OverviewTab() {
  const [period, setPeriod] = useState('7d');
  const { data, loading, error, refetch } = useFinanceSummary(period);
  const margins = useProductMarginsPreview(period, 5);

  const kpis = data?.kpis || {};
  const revenue     = Number(kpis.revenue || 0);
  const cogs        = Number(kpis.cogs || 0);
  const grossProfit = Number(kpis.grossProfit || 0);
  const expenses    = Number(kpis.expenses || 0);
  const netProfit   = Number(kpis.netProfit || 0);
  const netMargin   = Number(kpis.netMargin || 0);
  const orderCount  = Number(kpis.orderCount || 0);

  const isEmpty = !loading && !error && revenue === 0 && expenses === 0;

  return (
    <div className="fin-overview">
      <div className="fin-overview-toolbar">
        <PeriodSwitcher value={period} onChange={setPeriod} />
      </div>

      {error && <ErrorBanner message={error} onRetry={refetch} />}

      {isEmpty && !error && <EmptyOverview />}

      {!isEmpty && (
        <>
          <div className="fin-kpi-grid">
            <KpiCard
              label="Net sales"
              value={loading ? '' : fmtMoney(revenue)}
              hint={
                loading
                  ? ''
                  : `${orderCount} ${orderCount === 1 ? 'order' : 'orders'} · ex. tax / delivery / fees`
              }
              loading={loading}
            />
            <KpiCard
              label="Cost of goods"
              value={loading ? '' : fmtMoney(cogs)}
              hint={loading ? '' : 'COGS for this period'}
              loading={loading}
            />
            <KpiCard
              label="Gross profit"
              value={loading ? '' : fmtMoney(grossProfit)}
              hint={loading ? '' : 'Revenue − COGS'}
              tone="accent"
              loading={loading}
            />
            <KpiCard
              label="Expenses"
              value={loading ? '' : fmtMoney(expenses)}
              hint={loading ? '' : 'Logged this period'}
              loading={loading}
            />
            <KpiCard
              label="Net profit"
              value={loading ? '' : fmtMoney(netProfit)}
              hint={
                loading
                  ? ''
                  : netProfit >= 0
                  ? <span className="fin-trend-up"><TrendingUp size={12} /> Profitable</span>
                  : <span className="fin-trend-down"><TrendingDown size={12} /> Loss</span>
              }
              tone={netProfit >= 0 ? 'positive' : 'negative'}
              loading={loading}
            />
            <KpiCard
              label="Net margin"
              value={loading ? '' : fmtPct(netMargin)}
              hint={loading ? '' : 'Net profit ÷ revenue'}
              loading={loading}
            />
          </div>

          <div className="fin-overview-grid">
            <section className="fin-card fin-card--chart">
              <header className="fin-card-head">
                <h2 className="fin-card-title">Profit trend</h2>
                <p className="fin-card-sub">Daily gross profit</p>
              </header>
              {loading ? (
                <div className="fin-chart-skeleton" />
              ) : (
                <TrendChart data={data?.profitTrend} />
              )}
            </section>

            <section className="fin-card fin-card--list">
              <header className="fin-card-head">
                <h2 className="fin-card-title">Top product margins</h2>
                <p className="fin-card-sub">Highest gross-profit % this period</p>
              </header>
              <TopMarginsList
                items={margins.items}
                loading={margins.loading}
                error={margins.error}
              />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
